import { EvaluationRange } from '@/core/domain/resources/enums/EvaluationRange'

/**
 * Chart data structure for evaluation distribution
 */
export interface ChartDataset {
  label: string
  backgroundColor: string
  data: number[]
}

export interface ChartData {
  labels: string[]
  datasets: ChartDataset[]
}

/**
 * Statistics for evaluation results
 */
export interface EvaluationStats {
  notStarted: number
  defi: number
  effortsAFaire: number
  force: number
  total: number
}

/**
 * Teacher option for filters
 */
export interface TeacherOption {
  label: string
  value: string | null
}

/**
 * Chart colors for evaluation ranges
 */
export const EVALUATION_COLORS = {
  DEFI: '#FF6F61', // Red
  EFFORT_A_FAIRE: '#FFB84D', // Orange
  FORCE: '#4CAF50', // Green
  NOT_STARTED: '#9E9E9E' // Gray
} as const

/**
 * Get color for evaluation range
 */
export function getEvaluationColor(evalRange: string | null): string {
  if (!evalRange) return EVALUATION_COLORS.NOT_STARTED

  switch (evalRange) {
    case EvaluationRange.Values.DEFI:
      return EVALUATION_COLORS.DEFI
    case EvaluationRange.Values.EFFORT_A_FAIRE:
      return EVALUATION_COLORS.EFFORT_A_FAIRE
    case EvaluationRange.Values.FORCE:
      return EVALUATION_COLORS.FORCE
    default:
      return EVALUATION_COLORS.NOT_STARTED
  }
}

/**
 * Get label for evaluation range
 */
export function getEvaluationLabel(evalRange: string | null): string {
  if (!evalRange) return 'Non commencé'

  switch (evalRange) {
    case EvaluationRange.Values.DEFI:
      return 'Défi'
    case EvaluationRange.Values.EFFORT_A_FAIRE:
      return 'Efforts à faire'
    case EvaluationRange.Values.FORCE:
      return 'Force'
    default:
      return 'Non commencé'
  }
}

/**
 * Count evaluations by range from scores
 */
export function countEvaluationsByRange(
  scores: Array<{ evalRangeName: string | null }> | null
): EvaluationStats {
  const stats: EvaluationStats = {
    notStarted: 0,
    defi: 0,
    effortsAFaire: 0,
    force: 0,
    total: 0
  }

  if (!scores || scores.length === 0) return stats

  scores.forEach((score) => {
    stats.total++

    switch (score.evalRangeName) {
      case EvaluationRange.Values.DEFI:
        stats.defi++
        break
      case EvaluationRange.Values.EFFORT_A_FAIRE:
        stats.effortsAFaire++
        break
      case EvaluationRange.Values.FORCE:
        stats.force++
        break
      default:
        stats.notStarted++
    }
  })

  return stats
}

/**
 * Transform evaluation stats to chart data
 */
export function transformStatsToChartData(stats: EvaluationStats, labels: string[]): ChartData {
  return {
    labels,
    datasets: [
      {
        label: 'Défi',
        backgroundColor: EVALUATION_COLORS.DEFI,
        data: [stats.defi]
      },
      {
        label: 'Efforts à faire',
        backgroundColor: EVALUATION_COLORS.EFFORT_A_FAIRE,
        data: [stats.effortsAFaire]
      },
      {
        label: 'Force',
        backgroundColor: EVALUATION_COLORS.FORCE,
        data: [stats.force]
      }
    ]
  }
}

/**
 * Extract unique teachers from student data
 */
export function extractTeacherOptions(
  students: Array<{
    teacherFirstname: string | null
    teacherLastname: string | null
  }>
): TeacherOption[] {
  // Get unique teacher combinations
  const uniqueTeachers = new Set<string>()

  students.forEach((student) => {
    if (student.teacherFirstname && student.teacherLastname) {
      const fullName = `${student.teacherFirstname} ${student.teacherLastname}`
      uniqueTeachers.add(fullName)
    }
  })

  // Convert to options array
  const teacherOptions: TeacherOption[] = [{ label: 'Tous les enseignants', value: null }]

  Array.from(uniqueTeachers)
    .sort()
    .forEach((teacherName) => {
      teacherOptions.push({ label: teacherName, value: teacherName })
    })

  return teacherOptions
}

/**
 * Filter students by teacher
 */
export function filterStudentsByTeacher<
  T extends {
    teacherFirstname: string | null
    teacherLastname: string | null
  }
>(students: T[], selectedTeacher: string | null): T[] {
  if (!selectedTeacher) return students

  return students.filter((student) => {
    if (!student.teacherFirstname || !student.teacherLastname) return false
    const fullName = `${student.teacherFirstname} ${student.teacherLastname}`
    return fullName === selectedTeacher
  })
}

/**
 * Check if chart has meaningful data to display
 */
export function hasChartData(stats: EvaluationStats): boolean {
  return stats.defi > 0 || stats.effortsAFaire > 0 || stats.force > 0
}
